/**
 * Sticky banner shown across every signed-in page while the current
 * session is an impersonation (i.e. Better Auth's admin plugin set
 * `session.impersonatedBy` when minting the cookie).
 *
 * Rendering rule:
 *   - No session → nothing
 *   - Plain session → nothing
 *   - Impersonating session → orange stripe with the impersonated
 *     user's email + a "Return to super-admin" button that calls
 *     `authClient.admin.stopImpersonating()` and reloads.
 *
 * The banner lives in __root so it survives navigation between the
 * impersonated user's pages.
 */
import { useState } from 'react';
import { UserCog } from 'lucide-react';
import { authClient } from '../auth/client';

interface ImpersonatedSession {
  impersonatedBy?: string | null;
}

export function ImpersonationBanner() {
  const { data } = authClient.useSession();
  const [stopping, setStopping] = useState(false);

  const session = (data?.session ?? null) as ImpersonatedSession | null;
  const impersonating = Boolean(session?.impersonatedBy);

  if (!impersonating || !data?.user) return null;

  async function stop() {
    if (stopping) return;
    setStopping(true);
    try {
      await authClient.admin.stopImpersonating();
      window.location.href = '/sudo';
    } catch {
      setStopping(false);
    }
  }

  return (
    <div className="sticky top-0 z-50 border-b border-amber-700/40 bg-amber-500 text-amber-950">
      <div className="mx-auto flex max-w-6xl items-center justify-between gap-4 px-6 py-2 text-sm sm:px-12">
        <div className="flex items-center gap-2">
          <UserCog className="h-4 w-4" aria-hidden />
          <span>
            <span className="font-semibold">Impersonating</span>{' '}
            {data.user.name ? `${data.user.name} ` : ''}
            <span className="font-mono text-xs">({data.user.email})</span>
          </span>
        </div>
        <button
          type="button"
          onClick={stop}
          disabled={stopping}
          className="rounded-md bg-amber-950 px-3 py-1 text-xs font-medium text-amber-50 hover:bg-amber-900 disabled:opacity-60"
        >
          {stopping ? 'Returning…' : 'Return to super-admin'}
        </button>
      </div>
    </div>
  );
}
